import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { TrashIcon, PlusIcon, ArrowLeftIcon, PencilIcon, FunnelIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid'
import { FlightPayload, GliderPayload, MonthStats, PilotPayload } from './types'
import { FlightList } from './flight_list'
import { useAuthStatus } from './user_dropdown'
import { PilotAvatar } from './pilot_avatar'
import { Button } from '../components/button'
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '../components/dialog'
import { formatGliderDate, timeAgo, toDateInputValue } from './glider_status'
import { partitionGliders } from './glider_visibility'
import { PilotCountryWorldMap } from './pilot_country_world_map'

// Parse "Xh Ym" or "0h 5m" → total minutes
function parseDurationMinutes(d: string): number {
  const h = d.match(/(\d+)h/)
  const m = d.match(/(\d+)m/)
  return (h ? parseInt(h[1]) * 60 : 0) + (m ? parseInt(m[1]) : 0)
}

function formatMinutes(total: number): string {
  const h = Math.floor(total / 60)
  const m = total % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

function StatCard({ label, value, sub, href }: { label: string; value: string; sub?: string; href?: string }) {
  const inner = (
    <>
      <p className="text-xs font-semibold uppercase tracking-wider text-[#EF8354] mb-1">{label}</p>
      <p className="text-2xl font-bold text-[#2D3142] dark:text-white">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-[#4F5D75] dark:text-[#BFC0C0]">{sub}</p>}
    </>
  )
  if (href) {
    return (
      <a href={href} className="block bg-white dark:bg-[#2D3142] rounded-lg shadow px-5 py-4 hover:ring-2 hover:ring-[#EF8354]/40 transition-shadow">
        {inner}
      </a>
    )
  }
  return (
    <div className="bg-white dark:bg-[#2D3142] rounded-lg shadow px-5 py-4">
      {inner}
    </div>
  )
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const YEAR_COLORS = ['#4F5D75', '#BFC0C0', '#EF8354']

interface YearStats {
  flights: number
  totalMinutes: number
  totalDistanceKm: number
}

function FlightsBarChart({ flights, monthlyStats }: { flights: FlightPayload[]; monthlyStats?: MonthStats[] }) {
  const { data, years, yearStats } = useMemo(() => {
    const currentYear = new Date().getFullYear()
    const minYear = currentYear - 2

    if (monthlyStats && monthlyStats.length > 0) {
      const yearSet = new Set<number>()
      for (const s of monthlyStats) if (s.year >= minYear) yearSet.add(s.year)
      const years = Array.from(yearSet).sort()

      const counts: Record<string, Record<number, number>> = {}
      const minutes: Record<string, Record<number, number>> = {}
      for (const m of MONTHS) {
        counts[m] = {}; minutes[m] = {}
        for (const y of years) { counts[m][y] = 0; minutes[m][y] = 0 }
      }

      const yearStats: Record<number, YearStats> = {}
      for (const y of years) yearStats[y] = { flights: 0, totalMinutes: 0, totalDistanceKm: 0 }

      for (const s of monthlyStats) {
        if (s.year < minYear) continue
        const m = MONTHS[s.month - 1]
        counts[m][s.year] = s.flight_count
        minutes[m][s.year] = s.total_minutes
        yearStats[s.year].flights += s.flight_count
        yearStats[s.year].totalMinutes += s.total_minutes
        yearStats[s.year].totalDistanceKm += s.total_xc_distance
      }

      const data = MONTHS.map(m => {
        const row: Record<string, number | string> = { month: m }
        for (const y of years) { row[y] = counts[m][y]; row[`${y}_min`] = minutes[m][y] }
        return row
      })
      return { data, years, yearStats }
    }

    // Fallback: compute from paginated flight list.
    const yearSet = new Set<number>()
    for (const f of flights) {
      const y = new Date(f.start_time).getFullYear()
      if (y >= minYear) yearSet.add(y)
    }
    const years = Array.from(yearSet).sort()

    const counts: Record<string, Record<number, number>> = {}
    const minutes: Record<string, Record<number, number>> = {}
    for (const m of MONTHS) {
      counts[m] = {}; minutes[m] = {}
      for (const y of years) { counts[m][y] = 0; minutes[m][y] = 0 }
    }

    const yearStats: Record<number, YearStats> = {}
    for (const y of years) yearStats[y] = { flights: 0, totalMinutes: 0, totalDistanceKm: 0 }

    for (const f of flights) {
      const d = new Date(f.start_time)
      const y = d.getFullYear()
      if (y < minYear) continue
      const m = MONTHS[d.getMonth()]
      const mins = f.statistics?.duration ? parseDurationMinutes(f.statistics.duration) : 0
      counts[m][y] = (counts[m][y] ?? 0) + 1
      minutes[m][y] = (minutes[m][y] ?? 0) + mins

      const s = yearStats[y]
      s.flights += 1
      s.totalMinutes += mins
      s.totalDistanceKm += f.statistics?.scores?.['FAI'] ?? 0
    }

    const data = MONTHS.map(m => {
      const row: Record<string, number | string> = { month: m }
      for (const y of years) { row[y] = counts[m][y]; row[`${y}_min`] = minutes[m][y] }
      return row
    })
    return { data, years, yearStats }
  }, [flights, monthlyStats])

  const colorOf = (year: number) => {
    const idx = years.indexOf(year)
    return YEAR_COLORS[YEAR_COLORS.length - years.length + idx] ?? '#EF8354'
  }

  return (
    <div className="bg-white dark:bg-[#2D3142] rounded-lg shadow px-5 py-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#EF8354] mb-4">Flights per month</p>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} barCategoryGap="20%" barGap={2}>
          <XAxis
            dataKey="month"
            tick={{ fontSize: 11, fill: '#4F5D75' }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 10, fill: '#4F5D75' }}
            tickLine={false}
            axisLine={false}
            width={20}
          />
          <Tooltip
            cursor={{ fill: 'rgba(79,93,117,0.1)' }}
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null
              return (
                <div className="rounded border border-[#BFC0C0]/30 bg-white dark:bg-[#2D3142] px-3 py-2 text-xs shadow">
                  <p className="mb-1 font-medium text-[#2D3142] dark:text-white">{String(label)}</p>
                  {payload.map((p, i) => {
                    const count = p.value as number
                    const mins = (p.payload as Record<string, number>)[`${p.name}_min`] ?? 0
                    return (
                      <p key={i} style={{ color: p.fill as string }}>
                        {p.name}: {count} flight{count !== 1 ? 's' : ''}{mins > 0 ? `, ${formatMinutes(mins)}` : ''}
                      </p>
                    )
                  })}
                </div>
              )
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
            content={() => (
              <div className="flex flex-wrap items-start justify-center gap-x-6 gap-y-1 pt-2 text-[11px]">
                {years.map(year => {
                  const s = yearStats[year]
                  return (
                    <div key={year} className="flex items-center gap-1.5">
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-sm"
                        style={{ backgroundColor: colorOf(year) }}
                      />
                      <span className="font-medium" style={{ color: '#4F5D75' }}>
                        {year}
                      </span>
                      <span className="text-[#4F5D75]/70 dark:text-[#BFC0C0]/70">
                        — {s.flights} flight{s.flights !== 1 ? 's' : ''}, {formatMinutes(s.totalMinutes)}, {s.totalDistanceKm.toFixed(0)} km
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          />
          {years.map(year => (
            <Bar key={year} dataKey={String(year)} name={String(year)} fill={colorOf(year)} radius={[3, 3, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

const EMPTY_GLIDER = { name: '', brand: '', model: '', category: '', size: '', color: '', manufactured_at: '', last_control_at: '' }

function gliderDraftFromPayload(glider: GliderPayload) {
  return {
    name: glider.name ?? '',
    brand: glider.brand ?? '',
    model: glider.model ?? '',
    category: glider.category ?? '',
    size: glider.size ?? '',
    color: glider.color ?? '',
    manufactured_at: toDateInputValue(glider.manufactured_at),
    last_control_at: toDateInputValue(glider.last_control_at),
  }
}

function InlineGliderCell({
  value,
  type = 'text',
  displayValue,
  textClass = 'text-[#2D3142] dark:text-white',
  placeholder = '—',
  onSave,
}: {
  value: string
  type?: 'text' | 'date'
  displayValue?: string
  textClass?: string
  placeholder?: string
  onSave: (value: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [local, setLocal] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { setLocal(value) }, [value])
  useEffect(() => { if (editing) inputRef.current?.select() }, [editing])

  const commit = () => {
    setEditing(false)
    if (local !== value) onSave(local)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commit()
    if (e.key === 'Escape') { setLocal(value); setEditing(false) }
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type={type}
        value={local}
        onChange={e => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
        className={`w-full bg-transparent border-b-2 border-[#EF8354] px-0 py-0.5 text-sm outline-none ${textClass}`}
      />
    )
  }

  const shown = displayValue ?? (value.trim() || placeholder)

  return (
    <button
      onClick={() => setEditing(true)}
      className="group/cell inline-flex items-center gap-1.5 text-left"
    >
      <span className={`text-sm ${textClass} ${!value.trim() ? 'opacity-40' : ''}`}>{shown}</span>
      <PencilIcon className="h-3 w-3 shrink-0 text-[#4F5D75] dark:text-[#BFC0C0] opacity-0 group-hover/cell:opacity-100 transition-opacity" />
    </button>
  )
}

/** Format total minutes as a human-readable duration string. */
function fmtAirtime(totalMinutes: number): string {
  if (totalMinutes <= 0) return '—'
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

function GliderCard({
  glider,
  editable,
  onSaved,
  onDelete,
}: {
  glider: GliderPayload
  editable: boolean
  onSaved?: (glider: GliderPayload) => void
  onDelete?: (id: string) => void
}) {
  const [draft, setDraft] = useState(() => gliderDraftFromPayload(glider))
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [editingControl, setEditingControl] = useState(false)

  useEffect(() => {
    setDraft(gliderDraftFromPayload(glider))
    setSaveError('')
  }, [glider])

  const persistDraft = async (next: ReturnType<typeof gliderDraftFromPayload>, retired: boolean) => {
    if (!onSaved || !next.name.trim()) return false
    setSaving(true)
    setSaveError('')
    const payload = {
      name: next.name.trim(),
      brand: next.brand.trim(),
      model: next.model.trim(),
      category: next.category || undefined,
      size: next.size.trim(),
      color: next.color.trim(),
      retired,
      manufactured_at: next.manufactured_at ? new Date(`${next.manufactured_at}T00:00:00Z`).toISOString() : null,
      last_control_at: next.last_control_at ? new Date(`${next.last_control_at}T00:00:00Z`).toISOString() : null,
    }
    try {
      const res = await fetch(`/v0/gliders/${glider.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        setSaveError('Could not save wing changes.')
        return false
      }
      const data = await res.json()
      if (data.glider) {
        onSaved(data.glider)
      } else {
        onSaved({ ...glider, ...payload, category: payload.category ?? '', manufactured_at: payload.manufactured_at ?? undefined, last_control_at: payload.last_control_at ?? undefined })
      }
      return true
    } catch {
      setSaveError('Could not save wing changes.')
      return false
    } finally {
      setSaving(false)
    }
  }

  const persistField = async (key: string, value: string) => {
    const next = { ...draft, [key]: value }
    setDraft(next)
    await persistDraft(next, !!glider.retired)
  }

  const persistRetired = async () => {
    if (await persistDraft(draft, !glider.retired)) {
      setEditingControl(false)
    }
  }

  const setControlToday = () => {
    const today = new Date().toISOString().slice(0, 10)
    persistField('last_control_at', today)
    setEditingControl(false)
  }

  const stats = glider.stats
  const name = editable
    ? <InlineGliderCell value={draft.name} textClass="text-[#2D3142] dark:text-white font-semibold" onSave={v => persistField('name', v)} />
    : <span className="text-sm font-semibold text-[#2D3142] dark:text-white">{glider.name}</span>

  const subtitle = [glider.brand, glider.model].filter(Boolean).join(' ')
  const categoryLabel = draft.category || glider.category

  const field = (label: string, key: string, value: string, opts?: { type?: 'date'; displayValue?: string; textClass?: string }) =>
    editable
      ? (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4F5D75]/60 dark:text-[#BFC0C0]/60 mb-0.5">{label}</p>
          <InlineGliderCell
            value={value}
            type={opts?.type}
            displayValue={opts?.displayValue}
            textClass={opts?.textClass}
            onSave={v => persistField(key, v)}
          />
        </div>
      )
      : (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4F5D75]/60 dark:text-[#BFC0C0]/60 mb-0.5">{label}</p>
          <p className={`text-sm ${opts?.textClass ?? 'text-[#2D3142] dark:text-white'} ${!value.trim() ? 'opacity-40' : ''}`}>
            {opts?.displayValue ?? (value.trim() || '—')}
          </p>
        </div>
      )

  return (
    <div className="group/card relative rounded-lg border border-[#BFC0C0]/30 dark:border-[#4F5D75]/30 bg-white dark:bg-[#2D3142] shadow-sm transition-shadow hover:shadow-md">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 px-4 pt-3 pb-1.5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {name}
            {categoryLabel && (
              <span className="shrink-0 rounded bg-[#EF8354]/10 dark:bg-[#EF8354]/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#EF8354]">{categoryLabel}</span>
            )}
            {glider.retired && (
              <span className="shrink-0 rounded bg-[#4F5D75]/10 dark:bg-[#BFC0C0]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#4F5D75] dark:text-[#BFC0C0]">Retired</span>
            )}
          </div>
          {subtitle && <p className="text-xs text-[#4F5D75] dark:text-[#BFC0C0] mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {editable && (
            <button
              onClick={() => setEditingControl(prev => !prev)}
              className="text-[#4F5D75] dark:text-[#BFC0C0] hover:text-[#EF8354] dark:hover:text-[#EF8354] opacity-0 group-hover/card:opacity-100 transition-opacity"
              aria-label="Edit glider settings"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
          )}
          {editable && onDelete && (
            <button
              onClick={() => setConfirmDelete(true)}
              disabled={saving}
              className="text-[#4F5D75] dark:text-[#BFC0C0] hover:text-red-500 dark:hover:text-red-400 disabled:opacity-40 opacity-0 group-hover/card:opacity-100 transition-opacity"
              aria-label="Delete glider"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Edit panel */}
      {editingControl && editable && (
        <div className="mx-4 mb-2 space-y-2">
          <div className="flex items-center gap-2 rounded-md bg-[#4F5D75]/5 dark:bg-[#4F5D75]/15 px-2.5 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4F5D75]/60 dark:text-[#BFC0C0]/60 shrink-0">Category</p>
            <select
              value={draft.category}
              onChange={e => persistField('category', e.target.value)}
              className="flex-1 min-w-0 rounded border border-[#BFC0C0]/40 dark:border-[#4F5D75]/40 bg-transparent px-2 py-0.5 text-xs text-[#2D3142] dark:text-white outline-none focus:border-[#EF8354] transition-colors cursor-pointer"
            >
              <option value="">—</option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
              <option value="D">D</option>
              <option value="CCC">CCC</option>
            </select>
          </div>
          <div className="flex items-center gap-2 rounded-md bg-[#4F5D75]/5 dark:bg-[#4F5D75]/15 px-2.5 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4F5D75]/60 dark:text-[#BFC0C0]/60 shrink-0">Last control</p>
            <input
              type="date"
              value={draft.last_control_at}
              onChange={e => persistField('last_control_at', e.target.value)}
              className="flex-1 min-w-0 rounded border border-[#BFC0C0]/40 dark:border-[#4F5D75]/40 bg-transparent px-2 py-0.5 text-xs text-[#2D3142] dark:text-white outline-none focus:border-[#EF8354] transition-colors"
            />
            <button
              onClick={setControlToday}
              className="shrink-0 rounded bg-[#EF8354] px-2 py-0.5 text-[11px] font-medium text-white hover:bg-[#EF8354]/80 transition-colors"
            >
              Today
            </button>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-md bg-[#4F5D75]/5 dark:bg-[#4F5D75]/15 px-2.5 py-2">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4F5D75]/60 dark:text-[#BFC0C0]/60">Wing status</p>
              <p className="text-[11px] text-[#4F5D75] dark:text-[#BFC0C0]">
                {glider.retired ? 'Hidden from new flights' : 'Available for new flights'}
              </p>
            </div>
            <button
              onClick={persistRetired}
              disabled={saving}
              className="shrink-0 rounded border border-[#4F5D75]/30 dark:border-[#BFC0C0]/30 px-2 py-0.5 text-[11px] font-medium text-[#4F5D75] dark:text-[#BFC0C0] hover:border-[#EF8354] hover:text-[#EF8354] disabled:opacity-40 transition-colors"
            >
              {glider.retired ? 'Restore wing' : 'Retire wing'}
            </button>
          </div>
          {saveError && <p role="alert" className="text-[11px] text-red-600 dark:text-red-400">{saveError}</p>}
        </div>
      )}

      {/* Stats bar */}
      {stats && (stats.flight_count > 0 || stats.total_minutes > 0) && (
        <div className="mx-4 mb-2 space-y-1">
          <div className="flex items-center gap-3 rounded-md bg-[#EF8354]/8 dark:bg-[#EF8354]/12 px-2.5 py-1.5">
            <div className="flex items-center gap-1">
              <span className="text-sm font-bold text-[#EF8354]">{stats.flight_count}</span>
              <span className="text-[11px] text-[#4F5D75] dark:text-[#BFC0C0]">flight{stats.flight_count !== 1 ? 's' : ''}</span>
            </div>
            <div className="h-3 w-px bg-[#BFC0C0]/30 dark:bg-[#4F5D75]/30" />
            <div className="flex items-center gap-1">
              <span className="text-sm font-bold text-[#EF8354]">{fmtAirtime(stats.total_minutes)}</span>
              <span className="text-[11px] text-[#4F5D75] dark:text-[#BFC0C0]">airtime</span>
            </div>
          </div>
          {glider.last_control_at && (
            <div className="flex items-center gap-3 rounded-md bg-[#4F5D75]/5 dark:bg-[#4F5D75]/10 px-2.5 py-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#4F5D75]/60 dark:text-[#BFC0C0]/60">Since control</span>
              <div className="flex items-center gap-1">
                <span className="text-xs font-bold text-[#2D3142] dark:text-white">{stats.flights_since_control}</span>
                <span className="text-[10px] text-[#4F5D75] dark:text-[#BFC0C0]">flight{stats.flights_since_control !== 1 ? 's' : ''}</span>
              </div>
              <div className="h-2.5 w-px bg-[#BFC0C0]/30 dark:bg-[#4F5D75]/30" />
              <div className="flex items-center gap-1">
                <span className="text-xs font-bold text-[#2D3142] dark:text-white">{fmtAirtime(stats.airtime_since_control)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Details grid */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 px-4 pb-3">
        {field('Size', 'size', draft.size)}
        {field('Color', 'color', draft.color)}
        {field('Manufactured', 'manufactured_at', draft.manufactured_at, {
          type: 'date',
          displayValue: formatGliderDate(draft.manufactured_at || undefined),
        })}
        {draft.last_control_at && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4F5D75]/60 dark:text-[#BFC0C0]/60 mb-0.5">Last control</p>
            <p className="text-sm text-[#2D3142] dark:text-white cursor-default" title={formatGliderDate(draft.last_control_at)}>{timeAgo(draft.last_control_at)}</p>
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete glider?</DialogTitle>
        <DialogDescription>
          This will permanently remove <strong>{glider.name}</strong>. Flights linked to this glider will no longer reference it. This cannot be undone.
        </DialogDescription>
        <DialogActions>
          <Button plain onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</Button>
          <button
            onClick={async () => {
              setDeleting(true)
              await onDelete?.(glider.id)
              setDeleting(false)
              setConfirmDelete(false)
            }}
            disabled={deleting}
            className="inline-flex items-center rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-40 transition-colors"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

function GlidersPanel() {
  const [gliders, setGliders] = useState<GliderPayload[]>([])
  const [showRetired, setShowRetired] = useState(false)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState(EMPTY_GLIDER)
  const [saving, setSaving] = useState(false)
  const nameRef = useRef<HTMLInputElement>(null)
  const { active: activeGliders, retired: retiredGliders } = partitionGliders(gliders)

  const refreshGliders = () => {
    fetch('/v0/gliders')
      .then(r => r.json())
      .then(data => setGliders(data.gliders ?? []))
  }

  useEffect(() => {
    refreshGliders()
  }, [])

  // Re-fetch when the tab becomes visible again (e.g. after adding/deleting a flight).
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') refreshGliders() }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  const startAdding = () => {
    setForm(EMPTY_GLIDER)
    setAdding(true)
    setTimeout(() => nameRef.current?.focus(), 0)
  }

  const cancelAdding = () => {
    setAdding(false)
    setForm(EMPTY_GLIDER)
  }

  const handleAdd = async () => {
    if (!form.name.trim()) return
    setSaving(true)
    try {
      const res = await fetch('/v0/gliders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: crypto.randomUUID(),
          name: form.name.trim(),
          brand: form.brand.trim(),
          model: form.model.trim(),
          category: form.category || undefined,
          size: form.size.trim(),
          color: form.color.trim(),
          manufactured_at: form.manufactured_at ? new Date(`${form.manufactured_at}T00:00:00Z`).toISOString() : undefined,
          last_control_at: form.last_control_at ? new Date(`${form.last_control_at}T00:00:00Z`).toISOString() : undefined,
          retired: false,
        }),
      })
      if (!res.ok) return
      const updated = await fetch('/v0/gliders').then(r => r.json())
      setGliders(updated.gliders ?? [])
      setAdding(false)
      setForm(EMPTY_GLIDER)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    await fetch(`/v0/gliders/${id}`, { method: 'DELETE' })
    setGliders(prev => prev.filter(g => g.id !== id))
  }

  const handleSaved = (next: GliderPayload) => {
    setGliders(prev => prev.map(g => g.id === next.id ? next : g))
  }

  const addField = (key: keyof typeof EMPTY_GLIDER, placeholder: string, ref?: React.Ref<HTMLInputElement>) => (
    <div>
      <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#4F5D75]/60 dark:text-[#BFC0C0]/60 mb-0.5">{placeholder}</label>
      <input
        ref={ref}
        type={key.endsWith('_at') ? 'date' : 'text'}
        placeholder={placeholder}
        value={key.endsWith('_at') ? toDateInputValue(form[key]) : form[key]}
        onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
        onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') cancelAdding() }}
        className="w-full rounded border border-[#BFC0C0]/40 dark:border-[#4F5D75]/40 bg-transparent px-2 py-1 text-sm text-[#2D3142] dark:text-white outline-none focus:border-[#EF8354] transition-colors"
      />
    </div>
  )

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[#EF8354]">Gliders</h2>
        {!adding && (
          <button
            onClick={startAdding}
            className="flex items-center gap-1 text-xs font-medium text-[#4F5D75] dark:text-[#BFC0C0] hover:text-[#EF8354] transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Add glider
          </button>
        )}
      </div>

      {gliders.length === 0 && !adding ? (
        <p className="text-sm text-center text-[#4F5D75] dark:text-[#BFC0C0] py-6">
          No gliders yet.
        </p>
      ) : activeGliders.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {activeGliders.map(g => (
            <GliderCard
              key={g.id}
              glider={g}
              editable
              onSaved={handleSaved}
              onDelete={handleDelete}
            />
          ))}
        </div>
      ) : !adding ? (
        <p className="text-sm text-center text-[#4F5D75] dark:text-[#BFC0C0] py-6">
          No active gliders.
        </p>
      ) : null}

      {retiredGliders.length > 0 && (
        <div className="mt-4 border-t border-[#BFC0C0]/20 dark:border-[#4F5D75]/20 pt-3">
          <button
            type="button"
            aria-expanded={showRetired}
            onClick={() => setShowRetired(value => !value)}
            className="flex items-center gap-1.5 text-xs font-medium text-[#4F5D75] dark:text-[#BFC0C0] hover:text-[#EF8354] transition-colors"
          >
            {showRetired ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
            {showRetired ? 'Hide' : 'Show'} retired wings ({retiredGliders.length})
          </button>
          {showRetired && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {retiredGliders.map(g => (
                <GliderCard
                  key={g.id}
                  glider={g}
                  editable
                  onSaved={handleSaved}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add glider form as a card */}
      {adding && (
        <div className="mt-4 rounded-lg border border-dashed border-[#EF8354]/50 bg-white dark:bg-[#2D3142] shadow-sm px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#EF8354] mb-2">New Glider</p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
            {addField('name', 'Name *', nameRef)}
            {addField('brand', 'Brand')}
            {addField('model', 'Model')}
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#4F5D75]/60 dark:text-[#BFC0C0]/60 mb-0.5">Category</label>
              <select
                value={form.category}
                onChange={e => setForm(prev => ({ ...prev, category: e.target.value }))}
                className="w-full rounded border border-[#BFC0C0]/40 dark:border-[#4F5D75]/40 bg-transparent px-2 py-1 text-sm text-[#2D3142] dark:text-white outline-none focus:border-[#EF8354] transition-colors"
              >
                <option value="">—</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="D">D</option>
                <option value="CCC">CCC</option>
              </select>
            </div>
            {addField('size', 'Size')}
            {addField('color', 'Color')}
            {addField('manufactured_at', 'Manufactured')}
            {addField('last_control_at', 'Last control')}
          </div>
          <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-[#BFC0C0]/20 dark:border-[#4F5D75]/20">
            <button
              onClick={cancelAdding}
              className="text-xs text-[#4F5D75] dark:text-[#BFC0C0] hover:text-[#2D3142] dark:hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={!form.name.trim() || saving}
              className="rounded bg-[#EF8354] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#EF8354]/80 disabled:opacity-40 transition-colors"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function GlidersReadOnly({ nick }: { nick: string }) {
  const [gliders, setGliders] = useState<GliderPayload[]>([])

  useEffect(() => {
    setGliders([])
  }, [nick])

  if (gliders.length === 0) return null

  return (
    <div className="mb-8">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-[#EF8354] mb-4">Gliders</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {gliders.map(g => (
          <GliderCard key={g.id} glider={g} editable={false} />
        ))}
      </div>
    </div>
  )
}

function PilotGrid({ pilots }: { pilots: PilotPayload[] }) {
  if (pilots.length === 0) return (
    <p className="text-sm text-[#4F5D75] dark:text-[#BFC0C0]">None yet.</p>
  )
  return (
    <div className="flex flex-wrap gap-3">
      {pilots.map(p => (
        <a
          key={p.nick}
          href={`/pilot/${p.nick}`}
          className="flex items-center gap-2 rounded-lg px-3 py-2 bg-[#BFC0C0]/10 dark:bg-[#4F5D75]/20 hover:bg-[#EF8354]/10 dark:hover:bg-[#EF8354]/10 transition-colors"
        >
          <PilotAvatar pilot={p} className="size-7 bg-[#4F5D75]/20 text-[#4F5D75] dark:bg-[#BFC0C0]/20 dark:text-[#BFC0C0]" />
          <span className="text-sm font-medium text-[#2D3142] dark:text-white">{p.name}</span>
          <span className="text-xs text-[#4F5D75] dark:text-[#BFC0C0]">@{p.nick}</span>
        </a>
      ))}
    </div>
  )
}

const PAGE_SIZE = 50

export function PilotProfile() {
  const { nick } = useParams<{ nick: string }>()
  const navigate = useNavigate()
  const { userData } = useAuthStatus()
  const isOwnProfile = !!nick && userData.pilot?.nick === nick
  const canFollow = userData.authenticated && !isOwnProfile

  const [pilot, setPilot] = useState<PilotPayload | null>(null)
  const [flights, setFlights] = useState<FlightPayload[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [followers, setFollowers] = useState<PilotPayload[]>([])
  const [following, setFollowing] = useState<PilotPayload[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [isFollowing, setIsFollowing] = useState<boolean | null>(null)
  const [followBusy, setFollowBusy] = useState(false)

  const fetchPilotFlights = useCallback(async (nick: string, offset = 0, append = false) => {
    try {
      const response = await fetch(`/v0/pilots/${nick}/flights?limit=${PAGE_SIZE}&offset=${offset}`)
      const data = await response.json()
      const newFlights = data.flights ?? []
      setHasMore(data.has_more ?? false)
      if (append) {
        setFlights(prev => [...prev, ...newFlights])
      } else {
        setFlights(newFlights)
      }
    } catch {
      // silently fail for flight pagination
    } finally {
      setLoadingMore(false)
    }
  }, [])

  useEffect(() => {
    if (!nick) return
    setIsLoading(true)
    setNotFound(false)
    setIsFollowing(null)
    setFlights([])
    setHasMore(false)
    Promise.all([
      fetch(`/v0/pilots/${nick}`).then(r => { if (!r.ok) throw new Error('not found'); return r.json() }),
      fetch(`/v0/pilots/${nick}/flights?limit=${PAGE_SIZE}&offset=0`).then(r => r.json()),
      fetch(`/v0/pilots/${nick}/followers`).then(r => r.json()),
      fetch(`/v0/pilots/${nick}/following`).then(r => r.json()),
    ])
      .then(([pilotData, flightsData, followersData, followingData]: [PilotPayload, { flights: FlightPayload[]; has_more?: boolean }, { pilots: PilotPayload[] }, { pilots: PilotPayload[] }]) => {
        setPilot(pilotData)
        setFlights(flightsData.flights ?? [])
        setHasMore(flightsData.has_more ?? false)
        setFollowers(followersData.pilots ?? [])
        setFollowing(followingData.pilots ?? [])
      })
      .catch(() => setNotFound(true))
      .finally(() => setIsLoading(false))
  }, [nick])

  const handleLoadMore = useCallback(() => {
    if (!nick) return
    setLoadingMore(true)
    fetchPilotFlights(nick, flights.length, true)
  }, [nick, flights.length, fetchPilotFlights])

  // Fetch follow status when viewing another pilot's profile while authenticated
  useEffect(() => {
    if (!nick || !canFollow) return
    fetch(`/v0/pilots/${nick}/follow`, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error('not authed'); return r.json() })
      .then((data: { following: boolean }) => setIsFollowing(data.following))
      .catch(() => setIsFollowing(null))
  }, [nick, canFollow])

  const handleToggleFollow = async () => {
    if (!nick || isFollowing === null) return
    setFollowBusy(true)
    try {
      const method = isFollowing ? 'DELETE' : 'PUT'
      const res = await fetch(`/v0/pilots/${nick}/follow`, { method, credentials: 'include' })
      if (res.ok) {
        setIsFollowing(!isFollowing)
        // Refresh followers list to reflect the change
        fetch(`/v0/pilots/${nick}/followers`)
          .then(r => r.json())
          .then((d: { pilots: PilotPayload[] }) => setFollowers(d.pilots ?? []))
          .catch(() => {})
      }
    } catch (err) {
      console.error('Error toggling follow:', err)
    } finally {
      setFollowBusy(false)
    }
  }

  const stats = useMemo(() => {
    if (pilot?.stats) {
      const s = pilot.stats
      return {
        flightCount: s.flight_count,
        takeoffsVisited: s.takeoffs_visited ?? 0,
        countriesVisited: s.countries_visited ?? s.countries?.length ?? 0,
        countries: s.countries ?? [],
        totalMinutes: s.total_minutes,
        totalDistance: s.total_xc_distance,
        maxDistance: s.best_distance?.value ?? 0,
        maxDistanceId: s.best_distance?.flight_id ?? null,
        maxAltitude: s.max_altitude?.value ?? 0,
        maxAltitudeId: s.max_altitude?.flight_id ?? null,
        maxMinutes: s.longest_flight?.minutes ?? 0,
        maxMinutesId: s.longest_flight?.flight_id ?? null,
      }
    }
    if (!flights.length) return null
    let totalMinutes = 0
    let totalDistance = 0
    let maxDistance = 0
    let maxDistanceId: number | null = null
    let maxAltitude = 0
    let maxAltitudeId: number | null = null
    let maxMinutes = 0
    let maxMinutesId: number | null = null
    const takeoffIds = new Set<number>()
    const countryFlights = new Map<string, number>()

    for (const f of flights) {
      if (f.takeoff_id != null) takeoffIds.add(f.takeoff_id)
      if (f.takeoff_country) {
        const code = f.takeoff_country.trim().toUpperCase()
        if (code) countryFlights.set(code, (countryFlights.get(code) ?? 0) + 1)
      }
      const fai = f.statistics?.scores?.['FAI'] ?? 0
      totalDistance += fai
      if (fai > maxDistance) { maxDistance = fai; maxDistanceId = f.id }

      const alt = f.statistics?.top_altitude ?? 0
      if (alt > maxAltitude) { maxAltitude = alt; maxAltitudeId = f.id }

      const mins = f.statistics?.duration ? parseDurationMinutes(f.statistics.duration) : 0
      totalMinutes += mins
      if (mins > maxMinutes) { maxMinutes = mins; maxMinutesId = f.id }
    }

    const countries = Array.from(countryFlights, ([code, flight_count]) => ({ code, flight_count }))
      .sort((a, b) => a.code.localeCompare(b.code))
    return { flightCount: flights.length, takeoffsVisited: takeoffIds.size, countriesVisited: countries.length, countries, totalMinutes, totalDistance, maxDistance, maxDistanceId, maxAltitude, maxAltitudeId, maxMinutes, maxMinutesId }
  }, [pilot, flights])

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-[#BFC0C0]/30 rounded w-1/4" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-20 bg-[#BFC0C0]/30 rounded" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (notFound || !pilot) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <Button plain onClick={() => navigate('/')}>
          <ArrowLeftIcon />
          Back
        </Button>
        <div className="mt-8 text-center">
          <h2 className="text-lg font-semibold text-[#2D3142] dark:text-white">Pilot not found</h2>
          <p className="mt-2 text-sm text-[#4F5D75] dark:text-[#BFC0C0]">@{nick} does not exist.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      {/* Back link for other pilots' profiles */}
      {!isOwnProfile && (
        <div className="mb-6">
          <Button plain onClick={() => navigate(-1)}>
            <ArrowLeftIcon />
            Back
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="mb-8 flex items-center gap-4">
        <PilotAvatar
          pilot={pilot}
          className="size-16 bg-[#4F5D75]/20 text-[#4F5D75] dark:bg-[#BFC0C0]/20 dark:text-[#BFC0C0] ring-2 ring-[#EF8354]/30"
        />
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-[#2D3142] dark:text-white">{pilot.name}</h1>
          <p className="text-sm text-[#4F5D75] dark:text-[#BFC0C0]">@{pilot.nick}</p>
        </div>
        {canFollow && isFollowing !== null && (
          isFollowing ? (
            <Button
              plain
              disabled={followBusy}
              onClick={handleToggleFollow}
            >
              {followBusy ? 'Unfollowing…' : 'Unfollow'}
            </Button>
          ) : (
            <Button
              color="airtime"
              disabled={followBusy}
              onClick={handleToggleFollow}
            >
              {followBusy ? 'Following…' : 'Follow'}
            </Button>
          )
        )}
      </div>

      {/* Stat cards */}
      {stats && (
        <div className="mb-6 grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard
            label="Total flights"
            value={String(stats.flightCount ?? flights.length)}
            sub={`${stats.takeoffsVisited} take-off${stats.takeoffsVisited !== 1 ? 's' : ''} · ${stats.countriesVisited} countr${stats.countriesVisited === 1 ? 'y' : 'ies'}`}
          />
          <StatCard label="Total distance" value={`${stats.totalDistance.toFixed(0)} km`} sub="sum of FAI scores" />
          <StatCard label="Total flight time" value={formatMinutes(stats.totalMinutes)} />
          <StatCard label="Best distance" value={`${stats.maxDistance.toFixed(1)} km`} sub="single flight" href={stats.maxDistanceId != null ? `/flight/${stats.maxDistanceId}` : undefined} />
          <StatCard label="Max altitude" value={`${stats.maxAltitude} m`} href={stats.maxAltitudeId != null ? `/flight/${stats.maxAltitudeId}` : undefined} />
          <StatCard label="Longest flight" value={formatMinutes(stats.maxMinutes)} href={stats.maxMinutesId != null ? `/flight/${stats.maxMinutesId}` : undefined} />
        </div>
      )}

      {/* Countries flown map and flights per month chart */}
      {flights.length > 0 && stats && (
        <div className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(260px,0.8fr)_minmax(0,2fr)]">
          <PilotCountryWorldMap countries={stats.countries} />
          <FlightsBarChart flights={flights} monthlyStats={pilot?.stats?.monthly_stats} />
        </div>
      )}

      {/* Gliders — editable for own profile, read-only for others */}
      {isOwnProfile ? <GlidersPanel /> : <GlidersReadOnly nick={nick!} />}

      {/* Followers / Following */}
      <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#EF8354]">
            Followers <span className="text-[#4F5D75] dark:text-[#BFC0C0]">({followers.length})</span>
          </p>
          <PilotGrid pilots={followers} />
        </div>
        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#EF8354]">
            Following <span className="text-[#4F5D75] dark:text-[#BFC0C0]">({following.length})</span>
          </p>
          <PilotGrid pilots={following} />
        </div>
      </div>

      {/* Flights list */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#EF8354]">All flights</p>
          {flights.length > 0 && (
            <button
              onClick={() => navigate(`/flights?pilot=${encodeURIComponent(pilot.nick)}&pilot_name=${encodeURIComponent(pilot.name)}`)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[#4F5D75] hover:text-[#EF8354] dark:text-[#BFC0C0] dark:hover:text-[#EF8354] transition-colors"
              title="View in flights list with filters"
            >
              <FunnelIcon className="h-3.5 w-3.5" />
              Filter in flights
            </button>
          )}
        </div>
        {flights.length === 0 ? (
          <p className="text-sm text-center text-[#4F5D75] dark:text-[#BFC0C0]">No flights recorded yet.</p>
        ) : (
          <FlightList flights={flights} showAvatar={false} hasMore={hasMore} loadingMore={loadingMore} onLoadMore={handleLoadMore} />
        )}
      </div>
    </div>
  )
}

// Redirect /me → /pilot/:nick once we know who we are
export function MyProfile() {
  const { userData } = useAuthStatus()
  const navigate = useNavigate()

  useEffect(() => {
    if (userData.pilot?.nick) {
      navigate(`/pilot/${userData.pilot.nick}`, { replace: true })
    }
  }, [userData.pilot?.nick])

  // Show nothing while redirecting
  return null
}
